import { getStore, resetUnreadCountInStore } from 'core_main/store';
import { importAndInitializePubNub } from 'core_main/globals/vendors';
import { getSession, updateSessionMessages } from 'core_main/api/sessions';
import { ChatSessionsData } from 'core_main/api/sessions/types';
import { handleError } from 'globals/errors';
import { ReadMessage, MarkReadParameters } from './types';
import {
  runListenersForOnUnreadCountChangeEvent,
  validateMarkReadParams,
} from './helpers';

export const publishReadMessage = (session: ChatSessionsData) => {
  const { id } = session;
  const { channels } = session.attributes.pubnub.data;

  const convoMetaChannel = channels.find(
    (channel) => channel.type === 'meta-conversation',
  );

  return importAndInitializePubNub().then((module) => {
    const pubnub = module;
    const store = getStore();

    if (store.isPubNubConnected && convoMetaChannel) {
      const readMessage: ReadMessage = {
        message: {
          eventType: 'read',
        },
        channel: convoMetaChannel.name,
      };

      return pubnub
        .getMetaPubNub()
        ?.instance?.publish(readMessage)
        .then(() => {
          resetUnreadCountInStore(id);
          runListenersForOnUnreadCountChangeEvent(id);
        });
    }

    return undefined;
  });
};

const markSessionMessagesAsRead = ({
  messageIds,
  sessionId,
}: {
  messageIds: string[];
  sessionId: string;
}): Promise<null> => {
  return updateSessionMessages({
    messageIds,
    sessionId,
    body: { read: true },
  });
};

export const markRead = (parameters: MarkReadParameters) => {
  const store = getStore();
  const { sessions } = store;
  const sessionId = parameters?.conversationId;
  const messageIds = parameters?.messageIds;

  try {
    const validateMarkReadParamsError = validateMarkReadParams(sessionId);
    if (validateMarkReadParamsError) {
      throw validateMarkReadParamsError;
    }

    return Promise.resolve(sessions[sessionId])
      .then((sessionFromStore) => {
        if (sessionFromStore) return Promise.resolve(sessionFromStore);
        return getSession(sessionId).then((res) => res.data);
      })
      .then((session) => {
        const callsToMarkRead: Promise<null | void>[] = [
          publishReadMessage(session),
        ];
        if (messageIds?.length) {
          callsToMarkRead.push(
            markSessionMessagesAsRead({ messageIds, sessionId }),
          );
        }

        return Promise.all(callsToMarkRead);
      })
      .catch((error) => {
        handleError(error, {
          message: 'Failed to mark message as read',
        });
      });
  } catch (error) {
    handleError(error, {
      message: 'Failed to mark message as read',
    });
  }

  return undefined;
};
